import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Phone, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '@/components/shared/PageHeader';
import { SectionReveal } from '@/components/shared/SectionReveal';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { submitInquiry } from '@/lib/api';
import { toast } from 'sonner';
import { SEO } from '@/components/SEO';

export default function ContactPage() {
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    project_type: '',
    budget_range: '',
    message: '',
    timeline: '',
    shoot_location: '',
    production_dates: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Pre-fill form from navigation state
  useEffect(() => {
    if (location.state) {
      setFormData(prev => ({
        ...prev,
        ...(location.state.project_type && { project_type: location.state.project_type }),
        ...(location.state.message && { message: location.state.message })
      }));
    }
  }, [location.state]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError('Please fill in your name, email, and message.');
      return;
    }

    setSubmitting(true);
    try {
      await submitInquiry(formData);
      setSubmitted(true);
      toast.success('Inquiry submitted successfully');
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
      toast.error('Failed to submit inquiry');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div>
        <SEO 
          title="Contact Baklight Films — Production & Post Services in Mexico"
          description="Get in touch with Baklight for production services, post-production, and equipment rentals in Mexico. Response within 1-2 business days."
          path="/contact"
        />
        <div className="pt-40 pb-20 text-center">
          <div className="max-w-[500px] mx-auto px-5">
            <CheckCircle2 size={48} className="text-[hsl(var(--bk-success))] mx-auto mb-6" />
            <h1 className="font-display text-3xl md:text-4xl text-[hsl(var(--bk-fg))] mb-4" data-testid="contact-form-success-message">
              Thank you.
            </h1>
            <p className="font-body text-base text-[hsl(var(--bk-fg-muted))] leading-relaxed">
              We've received your inquiry and will respond within 1–2 business days. In the meantime, feel free to explore our work.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title="Contact Baklight Films — Production & Post Services in Mexico"
        description="Get in touch with Baklight for production services, post-production, and equipment rentals in Mexico. Response within 1-2 business days."
        path="/contact"
      />
      <PageHeader
        meta="Contact"
        title="Let's build something that matters."
        subtitle="Whether you're planning a production, need post-production, or want to discuss equipment — we're ready."
      />

      <section className="pb-20 sm:pb-28">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Left: Info */}
            <div className="lg:col-span-2">
              <SectionReveal>
                <div className="space-y-8">
                  <div>
                    <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Email</p>
                    <div className="flex flex-col gap-2">
                      <a href="mailto:info@baklight.com" className="font-body text-lg text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity">
                        info@baklight.com
                      </a>
                      <a href="mailto:paco@baklight.com" className="font-body text-lg text-[hsl(var(--bk-accent))] hover:opacity-80 transition-opacity">
                        paco@baklight.com
                      </a>
                    </div>
                  </div>
                  <div>
                    <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Location</p>
                    <div className="flex items-start gap-2">
                      <MapPin size={16} className="text-[hsl(var(--bk-fg-muted))] mt-0.5 flex-shrink-0" />
                      <p className="font-body text-base text-[hsl(var(--bk-fg-muted))]">
                        Guadalajara, Mexico<br />
                        Available worldwide
                      </p>
                    </div>
                  </div>
                  <div>
                    <p className="font-mono-meta text-[hsl(var(--bk-fg-subtle))] mb-3">Phone</p>
                    <div className="flex items-center gap-2">
                      <Phone size={16} className="text-[hsl(var(--bk-fg-muted))] flex-shrink-0" />
                      <p className="font-body text-base text-[hsl(var(--bk-fg-muted))]">+52 33 3808 3824</p>
                    </div>
                  </div>
                  <div className="pt-4 p-5 rounded-lg border border-[hsl(var(--bk-line))] bg-[hsl(var(--bk-bg-elev))]">
                    <p className="font-body text-sm text-[hsl(var(--bk-fg-muted))] leading-relaxed">
                      Response within 1–2 business days. For urgent inquiries, please reach out directly via email.
                    </p>
                  </div>
                </div>
              </SectionReveal>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <SectionReveal delay={0.1}>
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Name *</Label>
                      <Input
                        data-testid="contact-form-name-input"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))] focus:border-[hsl(var(--bk-accent))]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Email *</Label>
                      <Input
                        data-testid="contact-form-email-input"
                        type="email"
                        placeholder="your@email.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))] focus:border-[hsl(var(--bk-accent))]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Company</Label>
                    <Input
                      placeholder="Company or organization"
                      value={formData.company}
                      onChange={(e) => handleChange('company', e.target.value)}
                      className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))] focus:border-[hsl(var(--bk-accent))]"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Project Type</Label>
                      <Select value={formData.project_type} onValueChange={(v) => handleChange('project_type', v)}>
                        <SelectTrigger className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[50]">
                          <SelectItem value="production_mexico">Production Services in Mexico</SelectItem>
                          <SelectItem value="production">Production Services (Other)</SelectItem>
                          <SelectItem value="post">Post-Production</SelectItem>
                          <SelectItem value="rentals">Equipment Rentals</SelectItem>
                          <SelectItem value="full">Full Production</SelectItem>
                          <SelectItem value="general">General Inquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Budget Range</Label>
                      <Select value={formData.budget_range} onValueChange={(v) => handleChange('budget_range', v)}>
                        <SelectTrigger className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))]">
                          <SelectValue placeholder="Select range" />
                        </SelectTrigger>
                        <SelectContent className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] z-[50]">
                          <SelectItem value="under-25k">Under $25,000</SelectItem>
                          <SelectItem value="25k-50k">$25,000 – $50,000</SelectItem>
                          <SelectItem value="50k-100k">$50,000 – $100,000</SelectItem>
                          <SelectItem value="100k-250k">$100,000 – $250,000</SelectItem>
                          <SelectItem value="250k-plus">$250,000+</SelectItem>
                          <SelectItem value="tbd">To be determined</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Conditional Fields for Mexico Production */}
                  {formData.project_type === 'production_mexico' && (
                    <div className="space-y-6 p-6 rounded-lg border border-[hsl(var(--bk-accent))]/20 bg-gradient-to-br from-[hsl(var(--bk-accent))]/5 to-transparent">
                      <p className="font-mono-meta text-xs text-[hsl(var(--bk-accent))] uppercase tracking-wider">
                        Mexico Production Details
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Shoot Location (City/State)</Label>
                          <Input
                            data-testid="contact-form-shoot-location-input"
                            placeholder="e.g., Guadalajara, Jalisco"
                            value={formData.shoot_location}
                            onChange={(e) => handleChange('shoot_location', e.target.value)}
                            className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))] focus:border-[hsl(var(--bk-accent))]"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Estimated Production Dates</Label>
                          <Input
                            data-testid="contact-form-production-dates-input"
                            type="date"
                            value={formData.production_dates}
                            onChange={(e) => handleChange('production_dates', e.target.value)}
                            className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))] focus:border-[hsl(var(--bk-accent))] [color-scheme:dark]"
                            style={{ colorScheme: 'dark' }}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="font-mono-meta text-[hsl(var(--bk-fg-subtle))]">Message *</Label>
                    <Textarea
                      data-testid="contact-form-message-textarea"
                      placeholder="Tell us about your project..."
                      rows={5}
                      value={formData.message}
                      onChange={(e) => handleChange('message', e.target.value)}
                      className="bg-[hsl(var(--bk-bg-elev))] border-[hsl(var(--bk-line))] text-[hsl(var(--bk-fg))] placeholder:text-[hsl(var(--bk-fg-subtle))] focus:border-[hsl(var(--bk-accent))] resize-none"
                    />
                  </div>

                  {error && (
                    <p data-testid="contact-form-error-message" className="font-body text-sm text-[hsl(var(--bk-danger))]">{error}</p>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-form-submit-button"
                    disabled={submitting}
                    className="inline-flex items-center gap-2 font-body text-sm px-8 py-3 bg-[hsl(var(--bk-fg))] text-[hsl(var(--bk-bg))] rounded-[var(--radius)] hover:opacity-90 transition-opacity active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Send Inquiry'} <ArrowRight size={16} />
                  </button>
                </form>
              </SectionReveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
